import { DefaultPrimaryButton, DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { Package } from '@frontend/icons';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import { useKitReturnMethod } from './kit-return-method.controller';

const ID = 'kit-return-method';

export const KitReturnMethod = () => {
    const viewProps = useKitReturnMethod();
    const kitItemCount = viewProps.entries.reduce((total, entry) => total + entry.quantity, 0);
    const showLoading =
        viewProps.isLoading || (!viewProps.loadError && !!viewProps.equipment && !viewProps.hasReturnableKit && viewProps.singleReturnAvailable);

    return (
        <WizardScreenContainer
            id={ID}
            header={{
                onBack: viewProps.onBack
            }}
            footer={{
                onBack: viewProps.onBack
            }}
            containerClassName='bg-background-default flex flex-1 flex-col items-start justify-start gap-4 self-stretch overflow-y-scroll px-5 pt-6 pb-20'>
            {showLoading ? (
                <div className='flex flex-1 items-center justify-center self-stretch'>
                    <Spinner className='text-border-default fill-brand-primary size-12' />
                </div>
            ) : viewProps.loadError ? (
                <div className='bg-background-surface inline-flex items-center justify-between gap-4 self-stretch rounded-xl p-4'>
                    <div className='text-status-danger justify-start text-base font-medium'>
                        <FormattedMessage
                            id='kiosk.wizard.kit-return-check.load-error'
                            description='Error message shown when checking if equipment belongs to a kit failed'
                            defaultMessage='We could not check if this item belongs to a kit. Please try again.'
                        />
                    </div>
                    <button
                        type='button'
                        className='text-brand-default text-sm font-medium'
                        onClick={viewProps.retryLoad}>
                        <FormattedMessage
                            id='kiosk.wizard.kit-return-check.retry'
                            description='Button label for retrying the kit check'
                            defaultMessage='Retry'
                        />
                    </button>
                </div>
            ) : !viewProps.equipment ? (
                <div className='bg-background-surface inline-flex items-center justify-start self-stretch rounded-xl p-4'>
                    <div className='text-status-danger justify-start text-base font-medium'>
                        <FormattedMessage
                            id='kiosk.wizard.kit-return-check.missing-equipment'
                            description='Error message shown when the kit return check has no scanned equipment'
                            defaultMessage='No scanned item was found for this return.'
                        />
                    </div>
                </div>
            ) : !viewProps.hasReturnableKit && !viewProps.singleReturnAvailable ? (
                <div className='bg-background-surface inline-flex items-center justify-start self-stretch rounded-xl p-4'>
                    <div className='text-status-danger justify-start text-base font-medium'>
                        <FormattedMessage
                            id='kiosk.wizard.kit-return-method.single-return-config-error'
                            description='Error message shown when the single return route is missing'
                            defaultMessage='This return method is not configured correctly. Please contact support.'
                        />
                    </div>
                </div>
            ) : (
                <>
                    <div className='mb-2 flex items-start justify-start self-stretch'>
                        <div className='text-foreground-default justify-start text-base font-medium'>
                            <FormattedMessage
                                id='kiosk.wizard.kit-return-check.choice-title'
                                description='Title asking the user whether to return a kit or a single equipment'
                                defaultMessage='This item belongs to a kit. What would you like to return?'
                            />
                        </div>
                    </div>

                    <ProductCard
                        product_id={viewProps.equipment.product_id}
                        equipment={viewProps.equipment}
                    />

                    <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
                        <div className='flex w-full items-start justify-start gap-4'>
                            <div className='outline-border-default relative flex size-12 shrink-0 items-center justify-center rounded-full outline-2'>
                                <Package className='size-1/2' />
                            </div>
                            <div className='flex min-w-0 flex-1 flex-col items-start justify-center gap-1'>
                                <div className='text-foreground-default w-full text-base font-semibold wrap-break-word'>
                                    <FormattedMessage
                                        id='kiosk.wizard.kit-return-check.kit-option-title'
                                        description='Title for the kit return option'
                                        defaultMessage='Kit return'
                                    />
                                </div>
                                <div className='text-brand-muted w-full text-base font-medium wrap-break-word'>
                                    <FormattedMessage
                                        id='kiosk.wizard.kit-return-check.kit-option-subtitle'
                                        description='Subtitle describing how many equipment items are in the kit'
                                        defaultMessage='{count, plural, one {# item} other {# items}} in this kit'
                                        values={{ count: kitItemCount }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='grid w-full grid-cols-1 gap-3 sm:grid-cols-2'>
                            <DefaultSecondaryButton
                                text={
                                    <FormattedMessage
                                        id='kiosk.wizard.kit-return-check.single-button'
                                        description='Button for returning only the scanned equipment'
                                        defaultMessage='Single item'
                                    />
                                }
                                onClick={viewProps.onReturnSingle}
                                disabled={!viewProps.singleReturnAvailable}
                            />
                            <DefaultPrimaryButton
                                text={
                                    <FormattedMessage
                                        id='kiosk.wizard.kit-return-check.kit-button'
                                        description='Button for returning the full or partial kit'
                                        defaultMessage='Kit'
                                    />
                                }
                                onClick={viewProps.onReturnKit}
                            />
                        </div>
                    </div>
                </>
            )}
        </WizardScreenContainer>
    );
};

export default KitReturnMethod;
